import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  FormControlLabel,
  Switch,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import SizeEditorField from "./SizeEditorField";
import DietaryEditorField from "./DietaryEditorField";
// import ImageUploadField from "./ImageUploadField";

const GRAZE_BOX_CATEGORIES = [
  "Charcuterie",
  "Brunch",
  "Dessert",
  "Sweet",
  "Savory",
];

const emptyForm = {
  name: "",
  category: GRAZE_BOX_CATEGORIES[0],
  shortDescription: "",
  note: "",
  giftPackagingAvailable: false,
  sizes: [],
  dietaryOptions: [],
  images: {
    primary: "",
    secondary: [],
  },
};

const GrazeBoxFormModal = ({ open, onClose, onSave, initialData }) => {
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...emptyForm,
        ...initialData,
        sizes: Array.isArray(initialData.sizes) ? initialData.sizes : [],
        dietaryOptions: Array.isArray(initialData.dietaryOptions)
          ? initialData.dietaryOptions
          : [],
        images: {
          primary: initialData.images?.primary || "",
          secondary: initialData.images?.secondary || [],
        },
      });
    } else {
      setFormData(emptyForm);
    }
  }, [initialData]);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const hasMissingType = formData.dietaryOptions.some(
      (opt) => !opt.name || !opt.name.trim()
    );

    if (hasMissingType) {
      alert("Please fill out all dietary option types before saving.");
      return;
    }

    const sortedSizes = [...formData.sizes].sort((a, b) => a.sizeId - b.sizeId);

    const cleanedFormData = {
      ...formData,
      sizes: sortedSizes,
    };

    onSave(cleanedFormData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Graze Box" : "New Graze Box"}
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        {/* Basic Info */}
        <TextField
          label="Name"
          fullWidth
          value={formData.name}
          onChange={handleChange("name")}
        />

        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            value={formData.category || ""}
            label="Category"
            onChange={handleChange("category")}>
            {GRAZE_BOX_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Short Description"
          fullWidth
          multiline
          rows={2}
          value={formData.shortDescription}
          onChange={handleChange("shortDescription")}
        />

        <TextField
          label="Note"
          fullWidth
          multiline
          rows={2}
          value={formData.note}
          onChange={handleChange("note")}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.giftPackagingAvailable}
              onChange={handleChange("giftPackagingAvailable")}
            />
          }
          label="Gift Packaging Available"
        />

        <Divider sx={{ my: 2 }} />

        {/* Sizes */}
        <SizeEditorField
          sizes={formData.sizes}
          setSizes={(updater) =>
            setFormData((prev) => ({
              ...prev,
              sizes:
                typeof updater === "function" ? updater(prev.sizes) : updater,
            }))
          }
        />

        {/* Dietary Options */}
        <DietaryEditorField
          sizes={formData.sizes}
          dietaryOptions={formData.dietaryOptions}
          setDietaryOptions={(updater) =>
            setFormData((prev) => ({
              ...prev,
              dietaryOptions:
                typeof updater === "function"
                  ? updater(prev.dietaryOptions)
                  : updater,
            }))
          }
        />

        {/* Image Upload – placeholder */}
        <Box sx={{ mt: 4, fontStyle: "italic", color: "gray" }}>
          Image upload coming soon...
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrazeBoxFormModal;
